/**
 * CC-003 / US-156 — MRR & Churn Dashboard by Plan (F-207).
 * T-156-001: MRR breakdown by plan with churn alongside.
 * T-156-002: cohort curves + forecast with an alert when the forecast
 *            misses the current MRR (trend-negative projection).
 * Admin-only data (LP requireAdmin) — renders empty/error states gracefully.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { growthAnalytics, MrrData, ChurnData, Cohort } from '../../services/growthAnalytics';

const money = (v: number) =>
    v.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const pct = (v: number) => `${(v * 100).toFixed(1)}%`;

/** T-156-002: project next-month MRR from the reported trend (ratio vs prior period). */
export const forecastMrr = (mrr: MrrData): number => mrr.total * (1 + (mrr.trend || 0));

export const MrrDashboard: React.FC = () => {
    const [mrr, setMrr] = useState<MrrData | null>(null);
    const [churn, setChurn] = useState<ChurnData | null>(null);
    const [cohorts, setCohorts] = useState<Cohort[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [m, ch, co] = await Promise.all([
                growthAnalytics.getMrr(),
                growthAnalytics.getChurn(),
                growthAnalytics.getCohorts({ months: 6 }),
            ]);
            setMrr(m);
            setChurn(ch);
            setCohorts(Array.isArray(co) ? co : []);
        } catch (e: any) {
            setError(e?.message || 'Unable to load MRR data');
            setMrr(null);
            setChurn(null);
            setCohorts([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const forecast = mrr ? forecastMrr(mrr) : 0;
    const missAlert = !!mrr && !!mrr.total && forecast < mrr.total;
    const plans = mrr ? Object.entries(mrr.byPlan).filter(([, v]) => typeof v === 'number') : [];
    const maxPlan = plans.length ? Math.max(...plans.map(([, v]) => v), 1) : 1;

    return (
        <div className="max-w-7xl mx-auto px-4 py-8" data-testid="mrr-dashboard">
            <div className="flex items-center justify-between mb-6">
                <h1 className="text-2xl font-bold text-gray-800">MRR &amp; Churn by Plan</h1>
                <button onClick={load} className="bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700 text-sm">
                    Refresh
                </button>
            </div>

            {loading && <p className="text-gray-500" data-testid="mrr-loading">Loading MRR…</p>}
            {error && (
                <div className="bg-red-50 text-red-700 border border-red-200 rounded p-4 mb-6" data-testid="mrr-error">
                    {error}
                </div>
            )}

            {!loading && !error && !mrr && (
                <div className="bg-gray-50 border border-gray-200 rounded p-8 text-center text-gray-500" data-testid="mrr-empty">
                    No MRR data yet. Numbers appear once the first subscription events are recorded.
                </div>
            )}

            {!loading && !error && mrr && (
                <>
                    {missAlert && (
                        <div className="bg-amber-50 border border-amber-300 text-amber-800 rounded p-4 mb-6" data-testid="mrr-miss-alert" role="alert">
                            <strong>MRR miss forecast:</strong> projected next period {money(forecast)} is below
                            current {money(mrr.total)} (trend {pct(mrr.trend)}). Review churn and pipeline.
                        </div>
                    )}

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                        <div className="bg-white rounded-xl shadow p-5">
                            <p className="text-sm text-gray-500">Total MRR</p>
                            <p className="text-2xl font-bold text-gray-800" data-testid="mrr-total">{money(mrr.total)}</p>
                        </div>
                        <div className="bg-white rounded-xl shadow p-5">
                            <p className="text-sm text-gray-500">Trend vs prior period</p>
                            <p className={`text-2xl font-bold ${mrr.trend >= 0 ? 'text-green-600' : 'text-red-600'}`} data-testid="mrr-trend">
                                {mrr.trend >= 0 ? '▲' : '▼'} {pct(Math.abs(mrr.trend))}
                            </p>
                        </div>
                        <div className="bg-white rounded-xl shadow p-5">
                            <p className="text-sm text-gray-500">Forecast (next period)</p>
                            <p className="text-2xl font-bold text-gray-800" data-testid="mrr-forecast">{money(forecast)}</p>
                        </div>
                    </div>

                    <div className="bg-white rounded-xl shadow p-6 mb-6">
                        <h2 className="text-lg font-semibold mb-4">MRR by plan</h2>
                        <ul className="space-y-3" role="list" data-testid="mrr-by-plan">
                            {plans.map(([plan, value]) => (
                                <li key={plan} data-testid={`mrr-plan-${plan}`}>
                                    <div className="flex justify-between text-sm mb-1">
                                        <span className="font-medium text-gray-700">{plan}</span>
                                        <span className="text-gray-600">{money(value)}</span>
                                    </div>
                                    <div className="h-3 bg-gray-100 rounded overflow-hidden">
                                        <div
                                            className="h-3 bg-emerald-500 rounded"
                                            style={{ width: `${Math.max(2, (value / maxPlan) * 100)}%` }}
                                        />
                                    </div>
                                </li>
                            ))}
                            {plans.length === 0 && <li className="text-gray-500" data-testid="mrr-by-plan-empty">No plan-level data yet.</li>}
                        </ul>
                    </div>

                    <div className="bg-white rounded-xl shadow p-6 mb-6">
                        <h2 className="text-lg font-semibold mb-1">Churn</h2>
                        <p className="text-sm text-gray-500 mb-3">
                            Overall churn rate: <span className="font-semibold" data-testid="churn-overall">{pct(churn?.overallRate || 0)}</span>
                        </p>
                        <table className="w-full text-sm" data-testid="churn-by-plan">
                            <thead>
                                <tr className="text-left text-gray-500 border-b">
                                    <th className="py-2">Plan</th>
                                    <th className="py-2 text-right">Active at start</th>
                                    <th className="py-2 text-right">Cancelled</th>
                                    <th className="py-2 text-right">Rate</th>
                                </tr>
                            </thead>
                            <tbody>
                                {churn && Object.entries(churn.byPlan).map(([plan, c]) => (
                                    <tr key={plan} className="border-b last:border-0" data-testid={`churn-plan-${plan}`}>
                                        <td className="py-2">{plan}</td>
                                        <td className="py-2 text-right">{c.activeAtStart}</td>
                                        <td className="py-2 text-right">{c.cancelled}</td>
                                        <td className="py-2 text-right">{pct(c.rate)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="bg-white rounded-xl shadow p-6">
                        <h2 className="text-lg font-semibold mb-1">Cohort curves</h2>
                        <p className="text-sm text-gray-500 mb-3">Retention per signup cohort (M0 = signup month).</p>
                        {cohorts.length === 0 ? (
                            <p className="text-gray-500" data-testid="mrr-cohorts-empty">No cohort data yet.</p>
                        ) : (
                            <div className="overflow-x-auto" data-testid="mrr-cohort-table">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="text-left text-gray-500 border-b">
                                            <th className="py-2 pr-4">Cohort</th>
                                            <th className="py-2 pr-4 text-right">Size</th>
                                            <th className="py-2 pr-4 text-right">$ / user</th>
                                            {cohorts[0].retentionRate.map((_, i) => (
                                                <th key={i} className="py-2 px-2 text-right">M{i}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {cohorts.map((c) => (
                                            <tr key={c.cohortMonth} className="border-b last:border-0">
                                                <td className="py-2 pr-4 font-medium">{c.cohortMonth}</td>
                                                <td className="py-2 pr-4 text-right">{c.size}</td>
                                                <td className="py-2 pr-4 text-right">{c.mrrPerUser != null ? money(c.mrrPerUser) : '—'}</td>
                                                {c.retentionRate.map((r, i) => (
                                                    <td key={i} className="py-2 px-2 text-right">{pct(r)}</td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default MrrDashboard;
